import { useRef, useEffect, useState } from 'react';
import { computePosition, offset, flip, shift, autoUpdate, Placement } from '@floating-ui/dom';

const TOOLTIP_OFFSET = 15;

const BUTTON_ANCHORED_STEPS = [
  'return_home',
  'review_note_page',
  'transcript_page',
  'note_citations',
  'patient_visit_summary_page',
];

interface FloatingStyles {
  position: 'fixed';
  top: string;
  left: string;
}

interface UseTooltipFloatingOptions {
  referenceElement: HTMLElement | null;
  floatingElement: HTMLElement | null;
  position?: string | null;
  stepId: string;
  enabled: boolean;
}

function getPlacement(position: string | null | undefined, stepId: string): Placement {
  if (BUTTON_ANCHORED_STEPS.includes(stepId)) return 'right-start';
  if (stepId === 'leave_feedback' || stepId === 'save_upload_note') return 'left';
  if (stepId === 'precharting_overview' || stepId === 'precharting_custom_instructions') return 'right';
  if (stepId === 'precharting_optional_outline' || stepId === 'add_new_dot_phrase') return 'right-start';
  return (position as Placement) || 'bottom';
}

function getOffsetValue(stepId: string): number {
  if (stepId === 'start_recording') return 128;
  return TOOLTIP_OFFSET;
}

export function useTooltipFloating({
  referenceElement,
  floatingElement,
  position,
  stepId,
  enabled,
}: UseTooltipFloatingOptions) {
  const [styles, setStyles] = useState<FloatingStyles | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;

    if (!enabled || !referenceElement || !floatingElement) {
      setStyles(null);
      return;
    }

    const placement = getPlacement(position, stepId);
    const offsetValue = getOffsetValue(stepId);

    const updatePosition = async () => {
      if (!document.contains(referenceElement) || !document.contains(floatingElement)) {
        return;
      }

      const result = await computePosition(referenceElement, floatingElement, {
        strategy: 'fixed',
        placement,
        middleware: [
          offset(offsetValue),
          flip({ padding: 10 }),
          shift({ padding: 10, crossAxis: true }),
        ],
      });

      setStyles({
        position: 'fixed',
        top: `${result.y}px`,
        left: `${result.x}px`,
      });
    };

    cleanupRef.current = autoUpdate(referenceElement, floatingElement, updatePosition, {
      animationFrame: true,
    });

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [referenceElement, floatingElement, position, stepId, enabled]);

  return { floatingStyles: styles };
}
